import { PropsWithChildren, useEffect, useRef } from 'react'

import { useCaptureInteraction } from '@/app/_hooks/use-capture-interaction.js'

import { type ElementLocation, ElementLocationContext } from './element-location-context.js'

interface InteractionContextProps extends PropsWithChildren {
  elementLocation: ElementLocation
}

// This function is used to get the closest target element to the event target as some elements are nested like images inside links
const getClosestTarget = (target: HTMLElement) => {
  return target.closest('a') || target.closest('button')
}

export const InteractionContextProvider = ({ children, elementLocation }: InteractionContextProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { captureInteraction } = useCaptureInteraction()

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const customEvent = event as MouseEvent & { eventHandled?: boolean }

      // Prevent the event from being handled multiple times if we have this provider nested
      if (customEvent.eventHandled) return

      const target = event.target as HTMLElement

      const clickableElement = getClosestTarget(target)

      if (clickableElement) {
        const innerHtml = clickableElement.innerHTML
        const regex = /<img[^>]*\s+alt="([^"]*)"/i
        const match = innerHtml.match(regex)
        let srcUrl = '' as string | undefined

        if (match) {
          srcUrl = 'Image: ' + match[1]
        }

        const label = clickableElement.textContent?.trim() || srcUrl || '' // If the element is an image, use the alt text as the label
        const href = clickableElement.getAttribute('href') || ''

        const elementData = {
          section: elementLocation,
          label,
          href,
        }
        if (!!label || !!href) {
          captureInteraction(elementData)
          customEvent.eventHandled = true
        }
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener('click', handleClick)

      return () => {
        container.removeEventListener('click', handleClick)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run once
  }, [])

  return (
    <div ref={containerRef}>
      <ElementLocationContext.Provider value={elementLocation}>{children}</ElementLocationContext.Provider>
    </div>
  )
}
